import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Text, spaces } from '@electricjs/arc';
import { AIToolChip } from '@/components/AIChat/AIToolChip';
import { ApprovalButtons } from '@/components/AIChat/ApprovalButtons';
import { COPILOT_STATUS, TOOL_NAMES } from '@/helpers/aiChat/tools';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import useIntegrationAvailabilities from '@/hooks/useIntegrationAvailabilities';
import { ToolDisplayStatus } from '@/pages/AIChatCopilotKit/types';
import { useLazyGetOrganizationApplicationsQuery } from '@/redux/slices/organizationApiSlice';
import { HandleConnectResult } from './useConnectAppIntegrationTool';
import useApprovalKeyboardShortcuts from './useApprovalKeyboardShortcuts';

type ConnectAppIntegrationToolRendererProps = {
  args: Record<string, unknown>;
  status: string;
  respond?: (result: string) => void;
  handleConnect: (
    appName: string,
    existingSlug?: string,
    hasIntegration?: boolean
  ) => Promise<HandleConnectResult>;
};

const validateArgs = (
  args: Record<string, unknown>
): { appName: string } | null => {
  if (typeof args.app_name !== 'string' || !args.app_name) return null;
  return { appName: args.app_name };
};

export const ConnectAppIntegrationToolRenderer = ({
  args,
  status,
  respond,
  handleConnect,
}: ConnectAppIntegrationToolRendererProps) => {
  const organizationId = useGetOrganizationId();
  const availableIntegrations = useIntegrationAvailabilities();
  const [fetchOrganizationApplications, { data: orgData, isFetching }] =
    useLazyGetOrganizationApplicationsQuery();
  const [lookupDone, setLookupDone] = useState(false);
  const [wasRejected, setWasRejected] = useState(false);
  const [wasAlreadyConnected, setWasAlreadyConnected] = useState(false);

  const validatedArgs = useMemo(() => validateArgs(args), [args]);
  const appName = validatedArgs?.appName;
  const orgApp = orgData?.applications?.[0];

  const prevAppNameRef = useRef<string | undefined>();
  const prevOrgIdRef = useRef<string | undefined>();
  useEffect(() => {
    const appNameChanged =
      prevAppNameRef.current !== undefined &&
      prevAppNameRef.current !== appName;
    const orgIdChanged =
      prevOrgIdRef.current !== undefined &&
      prevOrgIdRef.current !== organizationId;
    if (appNameChanged || orgIdChanged) {
      setLookupDone(false);
      setWasAlreadyConnected(false);
    }
    if (appNameChanged) {
      setWasRejected(false);
    }
    prevAppNameRef.current = appName;
    prevOrgIdRef.current = organizationId;
  }, [appName, organizationId]);

  useEffect(() => {
    if (!(status === COPILOT_STATUS.EXECUTING && appName && organizationId))
      return;
    let mounted = true;
    fetchOrganizationApplications({ organizationId, name: appName })
      .then(() => {
        if (mounted) setLookupDone(true);
      })
      .catch(() => {
        if (mounted) setLookupDone(true);
      });
    return () => {
      mounted = false;
    };
  }, [status, appName, organizationId, fetchOrganizationApplications]);

  useEffect(() => {
    if (
      status === COPILOT_STATUS.EXECUTING &&
      lookupDone &&
      orgApp?.integrationConnected &&
      respond &&
      !wasAlreadyConnected
    ) {
      setWasAlreadyConnected(true);
      respond(
        `**${orgApp.application.name}** is already connected. You can view or manage it from its application page.`
      );
    }
  }, [status, lookupDone, orgApp, respond, wasAlreadyConnected]);

  const handleApprove = useCallback(async () => {
    if (!respond || !appName) return;
    try {
      if (orgApp) {
        const hasIntegration = availableIntegrations.includes(
          orgApp.application.integrationAvailability
        );
        const result = await handleConnect(
          appName,
          orgApp.application.slug,
          hasIntegration
        );
        respond(
          result.integrationOpened
            ? `Navigated to the **${orgApp.application.name}** application page and started the integration connection flow. Guide the admin through completing it.`
            : `Navigated to the **${orgApp.application.name}** application page.`
        );
      } else {
        const result = await handleConnect(appName);
        respond(
          result.integrationOpened
            ? `Added **${result.name}** to your organization, navigated to its page, and started the integration connection flow. Guide the admin through completing it.`
            : `Added **${result.name}** to your organization and navigated to its page.`
        );
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'An error occurred.';
      respond(`Error: ${message}`);
    }
  }, [respond, appName, orgApp, availableIntegrations, handleConnect]);

  const handleReject = useCallback(() => {
    if (!respond) return;
    setWasRejected(true);
    respond('Integration connection cancelled by user.');
  }, [respond]);

  useApprovalKeyboardShortcuts({
    onApprove: handleApprove,
    onDeny: handleReject,
    enabled:
      !!appName &&
      status === COPILOT_STATUS.EXECUTING &&
      !!respond &&
      lookupDone &&
      !wasAlreadyConnected,
  });

  // Report invalid args to CopilotKit as an effect to avoid state updates mid-render
  const isInvalid = !appName;
  useEffect(() => {
    if (isInvalid && respond && status === COPILOT_STATUS.EXECUTING) {
      respond(
        'Error: No application name provided. Please specify which app to connect.'
      );
    }
  }, [isInvalid, respond, status]);

  if (!appName) {
    return (
      <AIToolChip
        id={TOOL_NAMES.CONNECT_APP_INTEGRATION}
        toolSlug={TOOL_NAMES.CONNECT_APP_INTEGRATION}
        variant={
          status === COPILOT_STATUS.EXECUTING
            ? ToolDisplayStatus.REJECTED
            : ToolDisplayStatus.PENDING
        }
      />
    );
  }

  let displayStatus: ToolDisplayStatus;
  if (status === COPILOT_STATUS.EXECUTING && (isFetching || !lookupDone)) {
    displayStatus = ToolDisplayStatus.PENDING;
  } else if (
    status === COPILOT_STATUS.EXECUTING &&
    lookupDone &&
    wasAlreadyConnected
  ) {
    displayStatus = ToolDisplayStatus.COMPLETED;
  } else if (status === COPILOT_STATUS.EXECUTING && lookupDone) {
    displayStatus = ToolDisplayStatus.NEEDS_APPROVAL;
  } else if (status === COPILOT_STATUS.COMPLETE && wasRejected) {
    displayStatus = ToolDisplayStatus.REJECTED;
  } else if (status === COPILOT_STATUS.COMPLETE) {
    displayStatus = ToolDisplayStatus.COMPLETED;
  } else {
    displayStatus = ToolDisplayStatus.PENDING;
  }

  return (
    <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
      <AIToolChip
        id={TOOL_NAMES.CONNECT_APP_INTEGRATION}
        toolSlug={TOOL_NAMES.CONNECT_APP_INTEGRATION}
        variant={displayStatus}
      />
      {status === COPILOT_STATUS.EXECUTING &&
        respond &&
        lookupDone &&
        !wasAlreadyConnected && (
          <>
            {!orgApp && (
              <Text variant="body">
                <strong>{appName}</strong> isn&apos;t in your organization yet.
                Would you like to add it and navigate to its integration page?
              </Text>
            )}
            <ApprovalButtons
              onApprove={handleApprove}
              onReject={handleReject}
              style={{ flexDirection: 'row' }}
            />
          </>
        )}
    </Flex>
  );
};
